import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { userApi, User } from "../../services/userApi";
import { getTranslatedError } from "../../utils/errorUtils";

export const Users: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const fetchUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await userApi.getUsers({
        page,
        limit,
        search: search || undefined,
      });
      setUsers(response.users || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      setError(getTranslatedError(err, t, "admin.users.errors.fetch"));
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role: string): string => {
    return t(`admin.users.roles.${role}`) || role;
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && users.length === 0) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.users.title")}</Heading>

        {error && <Alert variant="error">{error}</Alert>}

        <Card padding="lg">
          <Stack spacing="md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  placeholder={t("admin.users.searchPlaceholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.name")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.email")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.username")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.role")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.users.createdAt")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                        {t("admin.users.noUsers")}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <button
                            onClick={() => navigate(`/admin/users/${user._id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {user.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.email || t("common.notAvailable")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => navigate(`/admin/users/${user._id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {user.username}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getRoleLabel(user.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {user.isBanned ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                                {t("admin.users.banned")}
                              </span>
                            ) : (
                              <>
                                {user.isApproved ? (
                                  <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                                    {t("admin.users.approved")}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800">
                                    {t("admin.users.pendingApproval")}
                                  </span>
                                )}
                                {user.isVerified ? (
                                  <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800">
                                    {t("admin.users.verified")}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800">
                                    {t("admin.users.notVerified")}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/users/${user._id}`)}
                            >
                              {t("admin.users.view")}
                            </Button>
                            {(user.role !== "admin" && user.role !== "store_admin") && (
                              <>
                                {user.isBanned ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      if (window.confirm(t("admin.users.confirmUnban", { name: user.name }))) {
                                        try {
                                          await userApi.unbanUser(user._id);
                                          fetchUsers();
                                        } catch (err: any) {
                                          setError(getTranslatedError(err, t, "admin.users.errors.unban"));
                                        }
                                      }
                                    }}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    {t("admin.users.unban")}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                      const reason = window.prompt(t("admin.users.banReasonPrompt"));
                                      if (reason !== null) {
                                        if (window.confirm(t("admin.users.confirmBan", { name: user.name }))) {
                                          try {
                                            await userApi.banUser(user._id, reason || undefined);
                                            fetchUsers();
                                          } catch (err: any) {
                                            setError(getTranslatedError(err, t, "admin.users.errors.ban"));
                                          }
                                        }
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    {t("admin.users.ban")}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {t("admin.users.showing", {
                    start: (page - 1) * limit + 1,
                    end: Math.min(page * limit, total),
                    total,
                  })}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t("common.previous")}
                  </Button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    {t("common.next")}
                  </Button>
                </div>
              </div>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};

export default Users;
